import React, { Dispatch, useState, useCallback, useMemo, useEffect } from 'react';
import styles from './SuperUnits.module.scss';
import IconRotating from 'src/assets/icons/rotating_arrow.svg?react';
import Header from 'src/components/header/Header';
import Spinner from 'src/components/spinner/Spinner';
import Search from 'src/components/search/unit-search/UnitSearch';
import Footer from 'src/components/footer/Footer';
import Button from 'src/components/button/Button';
import RadioGroup from "src/components/radio-group/RadioGroup";
import UnitSelectMulti from 'src/components/select-multi/unit-select/UnitSelectMulti';
import { useHistory } from 'react-router-dom';
import { useQueryDictionary } from '../../../hooks/useQueryDictionary';
import { useGetAllUnitsLazyQuery, UnitsByProgram, UnitContentFragment } from '../../../api/graphql-codegen';
import { Unit } from '../../../types';
import Card from './card/Card';
import UnitProgressModal from '@src/components/unit-progress-modal/UnitProgressModal';
import FooterPage from '@src/components/footer-page/FooterPage';

const emptyErrors = {
  name: '',
  endDate: '',
  courses: '',
  country: '',
  state: '',
  city: ''
};

export const sortOptions = [
  {label: "Unit Name", value: "0"},
  {label: "Country", value: "1"}
]

export const orderOptions = [
  {label: "Ascending", value: "0"},
  {label: "Descending", value: "1"}
]

const filterAndSort = (units: UnitContentFragment[], order: string, sortBy: string, keyword: string) =>
  units.filter(unit => unit.name.toLowerCase().includes(keyword))
  .sort((a, b) => {
    if (sortBy === '0') {
      if (a.name < b.name) { return order==='0' ? -1: 1; }
      if (a.name > b.name) { return order==='0' ? 1 : -1; }
    } else {
      if (a.country! < b.country! ) { return order==='0' ? -1: 1; }
      if (a.country! > b.country! ) { return order==='0' ? 1 : -1; }
    }
    return 0;
  })

function SuperUnits() {
  const [error, setError] = useState(emptyErrors);
  const [courses, setCourses] = useState<string[]>([]);

  const [keyword, setKeyword] = useState('');
  const [sortBy, setSortBy] = useState('0');
  const [order, setOrder] = useState('0');
  const [unitCount, setCount] = useState(0);
  const [showUnit, setShowUnit] = useState(false);
  const [selUnit, setSelUnit] = useState<Unit>();
  const history = useHistory();

  const coursesOptions = useQueryDictionary({ dictionary: 'Programs' });

  const [getAllUnits, {data: unitsData, loading: unitsLoading }] = useGetAllUnitsLazyQuery({
    variables: { input: { keyword: '' }}, fetchPolicy: 'network-only'
  });

  const units = useMemo(() => unitsData?.getAllUnits || [], [unitsData?.getAllUnits]);
  // const unitCount = useMemo(() => units.length, [units.length]);

  useEffect(() => {
    getAllUnits();
  }, [getAllUnits])

  const handleOpenNew = () => {
    history.push('/super/units/new');
  };

  const handleChangeSearch = useCallback((v) => {
    setKeyword(v);
  }, [setKeyword]);

  const handleResetFilters = useCallback(() => {
    setCourses([]);
    setKeyword('');
    setSortBy('0');
    setOrder('0');
  }, []);

  const handleChangeOptions = (setter: Dispatch<any>, key?: keyof typeof emptyErrors) => (value: any) => {
    if (key) {
      setError({
        ...error,
        [key]: '',
      });
    }
    setter(value);
  };

  const handleEditUnit = (unit: Unit) => {
    history.push(`/super/units/edit/${unit.id}`);
  };

  const handleOpenUnit = (unit: Unit) => {
    if (unit) {
      setShowUnit(true);
      setSelUnit(unit);
    }
  };

  const handleCloseModal = () => {setShowUnit(false)}

  const filtered = useMemo(() => {
    const s = keyword.toLowerCase();
    var newUnits: UnitsByProgram[] = [];
    var totalUnits = 0;
    units.forEach(item=>{
      var courseUnits = filterAndSort((item.units || []), order, sortBy, s);
      totalUnits = totalUnits + courseUnits.length;
      if (courseUnits.length > 0) {
        newUnits.push({id: item.id, name: item.name, units: [...courseUnits] })
      }
    })
    setCount(totalUnits)
    if (courses && courses.length > 0) {
      newUnits = newUnits.filter(course => course?.id && courses.includes(course?.id));
    }
    return newUnits
  }, [keyword, order, sortBy, units, courses, setCount]);

  const unitContent = (
    <div>
      {filtered.map((item, i) => {
        return (
          <div key={i}>
            <h2 className={styles.unitGroupTitle}>{item.name}</h2>
            <div className={styles.unitsDiv}>
              {((item as UnitsByProgram).units || []).map((unit, index) => {
                return (
                  <Card
                    key={index}
                    unit={unit}
                    handleEditUnit={handleEditUnit}
                    handleOpenUnit={handleOpenUnit}
                  />
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <>
      <Header />
      <div className={styles.page}>
        {unitsLoading ? <div className={styles.spinner}><Spinner/></div> : <>
          <div className={styles.actions}>
            <div className={styles.title}>Edit Units</div>
            <Button onClick={handleOpenNew} size={'small'} label={'Create Unit'}/>
          </div>
          <div className={styles.header}>
            <div className={styles.filters}>
              <Search onChange={handleChangeSearch} value={keyword}/>
              <UnitSelectMulti subTitle={error.courses} label={'Courses'}
                options={coursesOptions} inline={false} inDropDown={true}
                onChange={handleChangeOptions(setCourses, 'courses')}
                value={courses} error={!!error.courses}
              />
              <Button onClick={handleResetFilters} className={styles.resetBtn} label={'Reset'}
                    size={'extraSmall'} outline={true} color={'green'}
                    leftIcon={<IconRotating className={styles.resetIcon}/>}/>
            </div>
            <div className={styles.sort}>
                <div>
                  <div className={styles.sortTitle}>Sort by:</div>
                  <RadioGroup
                    value={sortBy}
                    options={sortOptions}
                    onChange={(value) => setSortBy(value)}
                    name={''}
                    hasError={false}
                    subTitle={""}
                  />
                </div>
                <div>
                  <div className={styles.sortTitle}>Order:</div>
                  <RadioGroup
                    value={order}
                    options={orderOptions}
                    onChange={(value) => setOrder(value)}
                    name={''}
                    hasError={false}
                    subTitle={""}
                  />
                </div>
              </div>
              <div  className={styles.count}>
                <div className={styles.unitsLength}>{unitCount} <div className={styles.unitsText}>Units</div></div>
              </div>
          </div>
          {
            unitContent
          }
        </>}
      </div>
      <FooterPage />
      {/* <Footer/> */}
      {selUnit?.id && (
        <UnitProgressModal open={showUnit} onClose={handleCloseModal} unitId={selUnit?.id} unitData={selUnit} />
      )}
    </>
  );
}

export default SuperUnits;
